/* eslint-disable react-hooks/exhaustive-deps */
/**
 * B040010 発注入力
 */

import { Column } from "@material-table/core";
import { Box } from "@mui/material";
import Big from "big.js";
import {
  B000111Biko,
  B000111Meisaibiko,
  B000170OtodokeNmShokuchi,
  B000190SokoNmrSokoshubetsuKbn,
  B040010Create,
  B040010Delete,
  B040010DeleteRequest,
  B040010HandleChangeShohinCd,
  B040010Shain,
  B040010Shire,
  B040010Shohin,
  B040010Tanka,
  B040010Update,
  B040010ZeiRitsu,
  B040020GetResponse,
  catchRestError,
} from "clients/rest";
import {
  msgState,
  SCancelButton,
  SCodeSelect,
  SConfirmDialog,
  SConfirmDialogAction,
  SDateField,
  SEntryTab,
  SFrame,
  SLabel,
  SNormalButton,
  SNumberField,
  STable,
  STextField,
} from "components";
import { getClientError, getClientMessage } from "messages";
import {
  B040010KanryoKbn,
  getSCode,
  HachuUchiwakeKbn,
  SCode,
} from "messages/codes";
import { labels } from "messages/labels";
import { B000111, B000140, B000170, B000190, B000200, B000220 } from "pages";
import { createRef, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { date, dateWithSlashAt, validDate } from "services";
import { digit } from "services/CalcService";
import {
  editKbn,
  hachuUchiwakeKbn,
  meishoKbn,
  shohinShokuchiKbn,
  shokuchiKbn,
  tenyuryokuKbn,
} from "services/codes";
import { focusState, useFocus } from "states";

export type B040010InputData = {
  editKbn: string;
  hachu: B040020GetResponse[];
};

// 呼び出し時のプロパティ
type Props = Partial<{}>;

// フィールド定義
type Fields = Partial<{
  editKbn: string;
  hachudenpyoNo: string;
  hachudenpyoNoF: string;
  hachuYmd: string;
  zeiRitsu: string;
  nyukayoteiYmd: string;
  shireCd: string;
  shireShokuchiKbn: number;
  shireNm: string;
  otodokeCd: string;
  otodokeShokuchiKbn: number;
  otodokeNm: string;
  shainCd: string;
  shainNm: string;
  bumonCd: string;
  bumonNm: string;
  eigyoCd: string;
  defaultSokoCd: string;
  defaultSokoNm: string;
  defaultSokoshubetsuKbn: string;
  bikoCd: string;
  biko: string;
  bikoCdBefore: string;
  version: string;
  hachuKinGokei: string;
  zeiKinGokei: string;
  gokei: string;
}>;

// 明細データ
type MeiData = Partial<{
  hachugyoNo: number;
  uchiwakeKbn: SCode;
  shohinCd: string;
  shohinShokuchiKbn: number;
  sokoCd: string;
  sokoNm: string;
  sokoshubetsuKbn: number;
  hachusu: string;
  shiresu: number;
  tenyuryokuKbn: number;
  basetanka: string;
  hachutanka: string;
  hachuKin: string;
  nyukayoteiYmd: string;
  shohinNm: string;
  meisaibikoCd: string;
  meisaibiko: string;
  meisaibikoCdBefore: string;
  kanryoKbn: string;
}>;

// 明細テーブルデータ定義
type Data = Partial<{
  hachugyoNo: number;
  uchiwakeKbnView: string;
  uchiwakeKbn: string;
  nyukayoteiYmd: string;
  shohinCd: string;
  shohinShokuchiKbn: number;
  shohinNm: string;
  hachusu: string;
  shiresu: number;
  tenyuryokuKbn: number;
  basetanka: string;
  hachutanka: string;
  hachuKin: string;
  sokoCd: string;
  sokoNm: string;
  sokoshubetsuKbn: number;
  meisaibikoCd: string;
  meisaibiko: string;
  meisaibikoCdBefore: string;
  kanryoKbn: string;
}>;

// フィールド初期値
const defaultFields: Fields = {
  editKbn: editKbn.register,
  hachudenpyoNo: "",
  hachudenpyoNoF: "",
  hachuYmd: date(),
  zeiRitsu: "",
  nyukayoteiYmd: date(),
  shireCd: "",
  shireShokuchiKbn: undefined,
  shireNm: "",
  otodokeCd: "",
  otodokeShokuchiKbn: undefined,
  otodokeNm: "",
  shainCd: "",
  shainNm: "",
  bumonCd: "",
  bumonNm: "",
  eigyoCd: "",
  defaultSokoCd: "",
  defaultSokoNm: "",
  defaultSokoshubetsuKbn: "",
  bikoCd: "",
  biko: "",
  bikoCdBefore: "",
  version: "",
  hachuKinGokei: "",
  zeiKinGokei: "",
  gokei: "",
};

// 明細フィールド初期値
const meiDefaultFields: HachuMeiFields = {
  meiTab: true,
  hachugyoNo: undefined,
  uchiwakeKbn: getSCode(HachuUchiwakeKbn, "0"),
  shohinCd: "",
  shohinShokuchiKbn: undefined,
  sokoCd: "",
  sokoNm: "",
  sokoshubetsuKbn: undefined,
  hachusu: "",
  shiresu: undefined,
  tenyuryokuKbn: undefined,
  basetanka: "",
  hachutanka: "",
  hachuKin: "",
  nyukayoteiYmd: "",
  shohinNm: "",
  meisaibikoCd: "",
  meisaibiko: "",
  meisaibikoCdBefore: "",
  kanryoKbn: "",
};

// テーブル設定
const columns: Column<Data>[] = [
  { field: "hachugyoNo", title: "行　", width: "4rem", align: "right" },
  {
    field: "uchiwakeKbnView",
    title: "区分",
    width: "4rem",
    render: (data) => {
      return getSCode(HachuUchiwakeKbn, data.uchiwakeKbn ?? "0")?.label;
    },
  },
  {
    field: "nyukayoteiYmd",
    title: "希望納期",
    width: "6rem",
    render: (data) => {
      if (data.nyukayoteiYmd! !== null && data.nyukayoteiYmd! !== "") {
        return dateWithSlashAt(data.nyukayoteiYmd!);
      }
      let backgroundColor = "inherit";
      if (Number(data.shiresu) > 0) {
        backgroundColor = "#FFFF00";
      }
      return <Box sx={{ backgroundColor }}>{data.nyukayoteiYmd}</Box>;
    },
  },
  { field: "shohinCd", title: "商品コード", width: "7rem" },
  { field: "shohinNm", title: "商品名", width: "15rem" },
  {
    field: "hachusu",
    title: "発注数　",
    width: "6rem",
    align: "right",
    render: (data) => digit(data.hachusu),
  },
  {
    field: "hachutanka",
    title: "発注単価　",
    width: "6rem",
    align: "right",
    render: (data) => {
      let backgroundColor = "inherit";
      if (data.tenyuryokuKbn === tenyuryokuKbn.exists) {
        backgroundColor = "#FFFF00";
      }
      return <Box sx={{ backgroundColor }}>{digit(data.hachutanka)}</Box>;
    },
  },
  {
    field: "hachuKin",
    title: "発注金額　",
    width: "6rem",
    align: "right",
    render: (data) => digit(data.hachuKin),
  },
  { field: "sokoCd", title: "倉庫コード", width: "6rem" },
  { field: "sokoNm", title: "倉庫名", width: "10rem" },
  { field: "meisaibikoCd", title: "発注明細備考コード", width: "8rem" },
  { field: "meisaibiko", title: "発注明細備考", width: "15rem" },
  { field: "meisaibikoCdBefore", hidden: true },
  {
    field: "kanryoKbnView",
    title: "完了",
    width: "4rem",
    render: (data) => {
      return getSCode(B040010KanryoKbn, data.kanryoKbn ?? "")?.label;
    },
  },
  { field: "kanryoKbn", title: "完了", hidden: true },
  { field: "sokoshubetsuKbn", hidden: true },
  { field: "uchiwakeKbn", title: "内訳区分", hidden: true },
  { field: "shohinShokuchiKbn", hidden: true },
  { field: "shiresu", hidden: true },
  { field: "tenyuryokuKbn", hidden: true },
  { field: "basetanka", hidden: true },
];

//明細入力部
export type HachuMeiFields = MeiData &
  Partial<{
    meiTab: boolean;
  }>;

const focusId = "B040010";

// B040010 発注入力
export const B040010 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};

const Page = (props: Props) => {
  // 初期設定
  // テーブル管理
  const tableRef = createRef();
  const [rows, setRows] = useState<Data[]>([]);
  const [rowCount, setRowCount] = useState(0);
  // テーブル行で選択されたデータ
  const [rowData, setRowData] = useState<Partial<Data>>();
  // フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });
  const meiMethods = useForm<HachuMeiFields>({
    mode: "onBlur",
    defaultValues: meiDefaultFields,
  });

  // フォーカス設定
  const { focus, register } = useRecoilValue(focusState(focusId));
  // 遷移元データ取得用
  const navigate = useNavigate();
  const location = useLocation();
  const inputData = location.state as B040010InputData;
  // メッセージ表示
  const setMsg = useSetRecoilState(msgState);
  // 登録ボタンクリック時に出す確認ダイアログオープン用
  const [openCreateConfirm, setOpenCreateConfirm] = useState(false);
  // 更新ボタンクリック時に出す確認ダイアログオープン用
  const [openUpdateConfirm, setOpenUpdateConfirm] = useState(false);

  // 活性設定
  // 参照モードかどうか
  const isReference = methods.watch("editKbn") === editKbn.reference;
  // 修正モードかどうか
  const isUpdate = methods.watch("editKbn") === editKbn.update;
  // 仕入先.諸口が「1：諸口」かどうか
  const isShireShokuchi =
    methods.watch("shireShokuchiKbn") === shokuchiKbn.shokuchi;
  // お届け先.諸口が「1：諸口」かどうか
  const isOtodokeShokuchi =
    methods.watch("otodokeShokuchiKbn") === shokuchiKbn.shokuchi;
  // 商品.諸口区分が「1:諸口」かどうか
  const isShohinShokuchiKbn =
    meiMethods.watch("shohinShokuchiKbn") === shohinShokuchiKbn.shokuchi;
  // 内訳区分が「2:値引」かどうか
  const isNebiki =
    meiMethods.watch("uchiwakeKbn")?.value === hachuUchiwakeKbn.nebiki;
  // ベース単価、発注単価が一致するかどうか(手入力発注区分)
  const isTenyuryokuhachuKbn =
    Number(meiMethods.watch("basetanka")) ===
    Number(meiMethods.watch("hachutanka"));
  // ボタンの活性・非活性
  const [disabledDeleteDetail, setDisabledDeleteDetail] = useState(true);

  // タブ切替
  const handleChangeMeiTab = () => {
    if (!meiMethods.watch("meiTab")) return;
    const fields = methods.getValues();
    meiMethods.reset();
    meiMethods.reset({
      ...meiMethods.getValues(),
      shohinCd: "",
    });
    meiMethods.setValue("nyukayoteiYmd", fields.nyukayoteiYmd);
    meiMethods.setValue("uchiwakeKbn", getSCode(HachuUchiwakeKbn, "0"));
    meiMethods.setValue("meiTab", true);

    setDisabledDeleteDetail(true);
  };

  // 初期表示(編集・参照時の設定)
  useEffect(() => {
    // unregisterを使用する値をresetで初期化(defalutValueが無視され、初期値にundifindが設定されることがあるため)
    methods.reset();
    methods.reset({
      ...methods.getValues(),
      shireCd: "",
      otodokeCd: "",
    });
    meiMethods.reset();
    meiMethods.reset({
      ...meiMethods.getValues(),
      shohinCd: "",
    });

    if (inputData === null) {
      return;
    }
    // 編集区分の設定
    methods.setValue("editKbn", inputData.editKbn);

    // 入力プロパティの反映
    const hachu = inputData.hachu;
    if (typeof hachu === "undefined") return;
    // 発注データ設定
    // ヘッダ部
    methods.setValue("hachudenpyoNo", inputData.hachu[0].hachudenpyoNo ?? "");
    methods.setValue("hachudenpyoNoF", inputData.hachu[0].hachudenpyoNoF ?? "");
    methods.setValue("hachuYmd", inputData.hachu[0].hachuYmd ?? "");
    methods.setValue(
      "nyukayoteiYmd",
      inputData.hachu[0].headerNyukayoteiYmd ?? ""
    );
    methods.setValue("shireCd", inputData.hachu[0].shireCd ?? "");
    methods.setValue(
      "shireShokuchiKbn",
      Number(inputData.hachu[0].shireShokuchiKbn)
    );
    methods.setValue("shireNm", inputData.hachu[0].shireNm ?? "");
    methods.setValue("otodokeCd", inputData.hachu[0].otodokeCd ?? "");
    methods.setValue(
      "otodokeShokuchiKbn",
      Number(inputData.hachu[0].otodokeShokuchiKbn)
    );
    methods.setValue("otodokeNm", inputData.hachu[0].otodokeNm ?? "");
    methods.setValue("shainCd", inputData.hachu[0].shainCd ?? "");
    methods.setValue("shainNm", inputData.hachu[0].shainNm ?? "");
    methods.setValue("bumonCd", inputData.hachu[0].bumonCd ?? "");
    methods.setValue("bumonNm", inputData.hachu[0].bumonNm ?? "");
    methods.setValue("eigyoCd", inputData.hachu[0].eigyoCd ?? "");
    methods.setValue("defaultSokoCd", inputData.hachu[0].defaultSokoCd ?? "");
    methods.setValue("defaultSokoNm", inputData.hachu[0].defaultSokoNm ?? "");
    methods.setValue(
      "defaultSokoshubetsuKbn",
      inputData.hachu[0].defaultSokoshubetsuKbn ?? ""
    );
    methods.setValue("bikoCd", inputData.hachu[0].bikoCd ?? "");
    methods.setValue("biko", inputData.hachu[0].biko ?? "");
    methods.setValue("bikoCdBefore", inputData.hachu[0].bikoCd ?? "");
    methods.setValue("version", inputData.hachu[0].version ?? "");

    // 明細部
    const gridDataList = inputData.hachu.map<Data>((value) => {
      const mei: Data = {
        hachugyoNo: value.hachugyoNo ?? "",
        uchiwakeKbn: value.uchiwakeKbn ?? "",
        uchiwakeKbnView: getSCode(HachuUchiwakeKbn, value.uchiwakeKbn)?.label,
        nyukayoteiYmd: value.nyukayoteiYmd ?? "",
        shohinCd: value.shohinCd ?? "",
        shohinShokuchiKbn: value.shohinShokuchiKbn ?? "",
        shohinNm: value.shohinNm ?? "",
        hachusu: value.hachusu ?? "",
        shiresu: Number(value.shiresu) ?? "",
        tenyuryokuKbn: Number(value.tenyuryokuKbn) ?? "",
        basetanka: value.basetanka ?? "",
        hachutanka: value.hachutanka ?? "",
        hachuKin: value.hachuKin ?? "",
        sokoCd: value.sokoCd ?? "",
        sokoNm: value.sokoNm ?? "",
        sokoshubetsuKbn: Number(value.sokoshubetsuKbn) ?? "",
        meisaibikoCd: value.meisaibikoCd ?? "",
        meisaibiko: value.meisaibiko ?? "",
        meisaibikoCdBefore: value.meisaibikoCd ?? "",
        kanryoKbn: value.kanryoKbn ?? "",
      };
      return mei;
    });
    // テーブルに反映
    setRows(gridDataList);
  }, []);

  // 初期表示(共通)
  useEffect(() => {
    (async () => {
      // 消費税取得
      await B040010ZeiRitsu(methods);

      // ヘッダ部.希望納期を明細入力部に設定
      const fields = methods.getValues();
      meiMethods.setValue("nyukayoteiYmd", fields.nyukayoteiYmd);
      meiMethods.setValue("uchiwakeKbn", getSCode(HachuUchiwakeKbn, "0"));

      // フォーカス
      focus(1);
    })();
  }, []);

  // 明細変更
  useEffect(() => {
    (async () => {
      // 消費税取得
      await B040010ZeiRitsu(methods);

      // 合計金額算出
      handleGokeiCalculating();
    })();
  }, [rows]);

  // 発注日変更時
  const handleChangeHachuYmd = (
    hachuYmd: string = methods.getValues().hachuYmd!
  ) => {
    if (hachuYmd === "" || !validDate(hachuYmd!)) return;

    (async () => {
      // 消費税取得
      await B040010ZeiRitsu(methods);

      // 明細入力部の単価再設定
      reacquisitionMeiTanka();

      // 明細が登録されている場合
      if (rows.length > 0) {
        // 明細再設定
        // 発注単価が変更されていない商品コードを取得(重複排除)
        const shohinCdList: string[] = Array.from(
          new Set(
            rows
              // 手入力区分をチェック
              .filter((row) => row.tenyuryokuKbn === tenyuryokuKbn.none)
              // 内訳区分をチェック
              .filter((row) => row.uchiwakeKbn !== hachuUchiwakeKbn.nebiki)
              .map((row) => row.shohinCd!)
          )
        );

        // 再設定する行が存在する場合
        if (shohinCdList.length > 0) {
          for (const shohinCd of shohinCdList) {
            B040010Tanka(methods, shohinCd, rows, navigate, setMsg, setRows);
          }
        }
        // 再設定する行が存在しない場合
        else {
          // 合計値(rowsに変更が無いため、明示的に処理を呼ぶ)
          handleGokeiCalculating();
        }
      }
    })();
  };

  // 明細入力部の単価再設定
  const reacquisitionMeiTanka = () => {
    // フォームのデータを取得する。
    const meiFields = meiMethods.getValues();
    // 明細入力部が設定されていない場合(商品コードで判断)
    if (meiFields.shohinCd === undefined) return;

    // 手入力区分が「1:あり」の場合
    if (meiFields.tenyuryokuKbn === tenyuryokuKbn.exists) return;

    // 内訳区分が「2:値引」の場合
    if (meiFields.uchiwakeKbn?.value === hachuUchiwakeKbn.nebiki) return;

    // 商品関連項目表示処理を行う
    B040010Shohin(methods, meiMethods, navigate, setMsg);
  };

  //  ダイアログ表示用（登録）
  const handleOpenCreateConfirm = () => {
    if (meiMethods.formState.isDirty) {
      setOpenCreateConfirm(true);
    } else {
      handleClickCreateButton();
    }
  };

  //  ダイアログ表示用（更新）
  const handleOpenUpdateConfirm = () => {
    if (meiMethods.formState.isDirty) {
      setOpenUpdateConfirm(true);
    } else {
      handleClickUpdateButton();
    }
  };

  // キャンセル押下時（登録）
  const handleCancelCreateConfirm = () => {
    setOpenCreateConfirm(false);
  };
  // キャンセル押下時（更新）
  const handleCancelUpdateConfirm = () => {
    setOpenUpdateConfirm(false);
  };

  // 登録
  const handleClickCreateButton = () => {
    // 独自チェック
    if (invalidated()) return;

    setOpenCreateConfirm(false);

    B040010Create(methods, rows)
      .then((response) => {
        methods.reset();
        methods.reset({
          ...methods.getValues(),
          shireCd: "",
          otodokeCd: "",
        });
        meiMethods.reset();
        meiMethods.reset({
          ...meiMethods.getValues(),
          shohinCd: "",
        });

        // ヘッダ部.希望納期を明細入力部に設定
        const fields = methods.getValues();
        meiMethods.setValue("nyukayoteiYmd", fields.nyukayoteiYmd);
        setRows([]);

        (async () => {
          // 消費税取得
          await B040010ZeiRitsu(methods);
          setMsg(getClientMessage("B040010_001", response.data.hachudenpyoNo));
        })();
      })
      .catch((reason) => {
        catchRestError(reason, navigate, setMsg);
      });
  };

  // 更新
  const handleClickUpdateButton = () => {
    // 独自チェック
    if (invalidated()) return;

    setOpenUpdateConfirm(false);

    B040010Update(methods, rows)
      .then(() => {
        methods.reset();
        methods.reset({
          ...methods.getValues(),
          shireCd: "",
          otodokeCd: "",
        });
        meiMethods.reset();
        meiMethods.reset({
          ...meiMethods.getValues(),
          shohinCd: "",
        });

        // ヘッダ部.希望納期を明細入力部に設定
        const fields = methods.getValues();
        meiMethods.setValue("nyukayoteiYmd", fields.nyukayoteiYmd);
        setRows([]);

        (async () => {
          // 消費税取得
          await B040010ZeiRitsu(methods);
          setMsg(getClientMessage("B040010_002"));
        })();
      })
      .catch((reason) => {
        catchRestError(reason, navigate, setMsg);
      });
  };

  // 明細登録
  const handleClickCreateMeiButton = () => {
    // 独自チェック
    if (meiInvalidated()) return;

    // フィールドチェック
    meiMethods.handleSubmit(() => {
      // 明細に設定するデータを取得
      const hachugyoNo: number = rows.length + 1;
      const data: Data = handleDataMapping(hachugyoNo);
      // 明細を設定
      setRows([...rows, data]);
      setRowCount(hachugyoNo);

      // 明細クリア
      meiMethods.reset();
      meiMethods.reset({
        ...meiMethods.getValues(),
        shohinCd: "",
      });

      // ヘッダ部.希望納期を明細入力部に設定
      const fields = methods.getValues();
      meiMethods.setValue("nyukayoteiYmd", fields.nyukayoteiYmd);
      // 明細削除ボタン非活性
      setDisabledDeleteDetail(true);

      focus(12);
    })();
  };

  // 明細更新
  const handleClickUpdateMeiButton = () => {
    // 独自チェック
    if (meiInvalidated()) return;

    // フィールドチェック
    meiMethods.handleSubmit(() => {
      const hachugyoNo: number = rowData?.hachugyoNo!;
      // 明細に設定するデータを取得
      const data: Data = handleDataMapping(hachugyoNo);
      // 明細を設定
      rows[hachugyoNo - 1] = data;
      setRows([...rows]);

      // 明細クリア
      meiMethods.reset();
      meiMethods.reset({
        ...meiMethods.getValues(),
        shohinCd: "",
      });

      // ヘッダ部.希望納期を明細入力部に設定
      const fields = methods.getValues();
      meiMethods.setValue("nyukayoteiYmd", fields.nyukayoteiYmd);
      // 明細削除ボタン非活性
      setDisabledDeleteDetail(true);
    })();
  };

  // 入力チェック
  const invalidated = (): boolean => {
    // フォームのデータを取得する。
    const fields = methods.getValues();

    // [発注日 <= ヘッダー.希望納期]ではない場合
    // エラーメッセージをメッセージ欄に表示する。
    if (!(fields.hachuYmd! <= fields.nyukayoteiYmd!)) {
      setMsg(getClientError("B040010_001"));
      return true;
    }
    return false;
  };

  // 明細入力部の入力チェック
  const meiInvalidated = (): boolean => {
    // フォームのデータを取得する。
    const fields = methods.getValues();
    const meiFields = meiMethods.getValues();

    // 内訳区分=0(通常)・3(見本)の場合
    // 発注数、発注単価が未入力の場合エラーメッセージをメッセージ欄に表示する。
    if (
      meiFields.uchiwakeKbn?.value === hachuUchiwakeKbn.tujo ||
      meiFields.uchiwakeKbn?.value === hachuUchiwakeKbn.mihon
    ) {
      if (!meiFields.hachusu) {
        setMsg(getClientError("B040010_002"));
        return true;
      }
      if (meiFields.hachutanka === "") {
        setMsg(getClientError("B040010_003"));
        return true;
      }
    }

    // [発注日 <= 希望納期]ではない場合
    // エラーメッセージをメッセージ欄に表示する。
    if (!(fields.hachuYmd! <= meiFields.nyukayoteiYmd!)) {
      setMsg(getClientError("B040010_001"));
      return true;
    }

    return false;
  };

  // 明細のデータマッピング;
  const handleDataMapping = (hachugyoNo: number): Data => {
    // フォームのデータを取得する。
    const meiFields = meiMethods.getValues();
    const data: Data = {
      hachugyoNo,
      uchiwakeKbnView: meiFields.uchiwakeKbn?.label,
      uchiwakeKbn: meiFields.uchiwakeKbn?.value,
      shohinCd: meiFields.shohinCd,
      shohinShokuchiKbn: meiFields.shohinShokuchiKbn,
      sokoCd: meiFields.sokoCd,
      sokoNm: meiFields.sokoNm,
      sokoshubetsuKbn: meiFields.sokoshubetsuKbn,
      hachusu: meiFields.hachusu,
      shiresu: meiFields.shiresu,
      // 手入力発注区分の設定(一致する場合「0：変更なし」、一致しない場合「1：変更あり」)
      tenyuryokuKbn: isTenyuryokuhachuKbn
        ? tenyuryokuKbn.none
        : tenyuryokuKbn.exists,
      basetanka: meiFields.basetanka,
      hachutanka: meiFields.hachutanka,
      hachuKin: meiFields.hachuKin,
      nyukayoteiYmd: meiFields.nyukayoteiYmd,
      shohinNm: meiFields.shohinNm,
      meisaibikoCd: meiFields.meisaibikoCd,
      meisaibiko: meiFields.meisaibiko,
      meisaibikoCdBefore: meiFields.meisaibikoCdBefore,
      kanryoKbn: meiFields.kanryoKbn,
    };

    return data;
  };

  // 発注削除イベント
  const handleClickDeleteButton = () => {
    // フォームのデータを取得する。
    const fields = methods.getValues();
    // リクエスト作成
    const param: B040010DeleteRequest = {
      hachudenpyoNo: fields.hachudenpyoNo,
    };
    // API呼び出し
    B040010Delete(param)
      .then(() => {
        methods.reset();
        methods.reset({
          ...methods.getValues(),
          shireCd: "",
          otodokeCd: "",
        });
        meiMethods.reset();
        meiMethods.reset({
          ...meiMethods.getValues(),
          shohinCd: "",
        });
        setRows([]);
        setMsg(getClientMessage("B040010_003"));
      })
      .catch((reason) => {
        catchRestError(reason, navigate, setMsg);
      });
  };

  // 明細削除
  const handleClickDeleteMeiButton = () => {
    // 選択行の取得
    if (typeof rowData?.hachugyoNo === "undefined") return;
    const hachugyoNo: number = rowData.hachugyoNo;
    // 対象の行を削除
    const newRows = rows
      .filter(function (source: Data) {
        return source.hachugyoNo !== hachugyoNo;
      })
      // 明細の連番の振り直し
      .map((meiRow: any, index) => {
        // 明細
        meiRow.hachugyoNo = ++index;
        return meiRow;
      });

    setRows(newRows);

    // 行選択の解除
    setRowData(undefined);

    // 明細クリア
    meiMethods.reset();
    meiMethods.reset({
      ...meiMethods.getValues(),
      shohinCd: "",
    });

    // ヘッダ部.希望納期を明細入力部に設定
    const fields = methods.getValues();
    meiMethods.setValue("nyukayoteiYmd", fields.nyukayoteiYmd);

    // 明細削除ボタン非活性
    setDisabledDeleteDetail(true);

    // 明細入力部を新規登録に変更する
    meiMethods.setValue("meiTab", true);
  };

  //合計金額算出
  const handleGokeiCalculating = () => {
    // 明細が設定されていない場合
    if (rows.length === 0) {
      // 合計金額の初期化のみ行う
      methods.setValue("hachuKinGokei", "");
      methods.setValue("zeiKinGokei", "");
      methods.setValue("gokei", "");
      return;
    }

    // フォームのデータを取得する。
    const fields = methods.getValues();

    // 発注金額合計
    const hachuKinGokei: Big = rows.reduce((acc: Big, row: Data): Big => {
      if (row.uchiwakeKbn === hachuUchiwakeKbn.nebiki) {
        return acc.minus(new Big(row.hachuKin ? row.hachuKin : "0"));
      } else {
        return acc.plus(new Big(row.hachuKin ? row.hachuKin : "0"));
      }
    }, new Big("0"));

    // 消費税合計
    let zeiKinGokei: Big = new Big(0);
    zeiKinGokei = hachuKinGokei
      .times(new Big(fields.zeiRitsu ? fields.zeiRitsu : "0"))
      .div(new Big("100"))
      .round(0, Big.roundDown);

    // 合計
    const gokei: Big = hachuKinGokei.plus(zeiKinGokei);

    methods.setValue("hachuKinGokei", hachuKinGokei.toString());
    methods.setValue("zeiKinGokei", zeiKinGokei.toString());
    methods.setValue("gokei", gokei.toString());
  };

  // テーブル行ダブルクリックイベント
  const handleDoubleClickTableRow = (data: Data) => {
    // フォームのデータを取得する。
    const meiFields = meiMethods.getValues();

    // [明細データ.仕入数 > 0]の場合
    if (meiFields.shiresu! > 0) {
      // エラーメッセージをメッセージ欄に表示する。
      setMsg(getClientError("B040010_006"));
      return;
    }

    // データを保存
    setRowData(data);
    if (typeof data.hachugyoNo !== "undefined") {
      // 明細入力部を編集に変更する
      meiMethods.setValue("meiTab", false);
      // 明細削除ボタンを活性化
      setDisabledDeleteDetail(false);
      // 明細入力部に値を設定する
      meiMethods.setValue("hachugyoNo", data.hachugyoNo);
      meiMethods.setValue(
        "uchiwakeKbn",
        getSCode(HachuUchiwakeKbn, data.uchiwakeKbn ?? "0")
      );
      meiMethods.setValue("shohinCd", data.shohinCd);
      meiMethods.setValue("shohinShokuchiKbn", data.shohinShokuchiKbn);
      meiMethods.setValue("sokoCd", data.sokoCd);
      meiMethods.setValue("sokoNm", data.sokoNm);
      meiMethods.setValue("sokoshubetsuKbn", data.sokoshubetsuKbn);
      meiMethods.setValue("hachusu", data.hachusu);
      meiMethods.setValue("shiresu", data.shiresu);
      meiMethods.setValue("tenyuryokuKbn", data.tenyuryokuKbn);
      meiMethods.setValue("basetanka", data.basetanka);
      meiMethods.setValue("hachutanka", data.hachutanka);
      meiMethods.setValue("hachuKin", data.hachuKin);
      meiMethods.setValue("nyukayoteiYmd", data.nyukayoteiYmd);
      meiMethods.setValue("shohinNm", data.shohinNm);
      meiMethods.setValue("meisaibikoCd", data.meisaibikoCd);
      meiMethods.setValue("meisaibiko", data.meisaibiko);
      meiMethods.setValue("meisaibikoCdBefore", data.meisaibikoCdBefore);
      meiMethods.setValue("kanryoKbn", data.kanryoKbn);
      focus(12);
    }
  };

  // 内訳区分変更時
  const handleChangeUchiwakeKbn = () => {
    // ヘッダ部.希望納期を明細入力部に設定
    const fields = methods.getValues();
    const { uchiwakeKbn } = meiMethods.getValues();
    // 明細入力欄を全て初期化する。
    meiMethods.reset();
    meiMethods.reset({
      ...meiMethods.getValues(),
      shohinCd: "",
    });
    meiMethods.setValue("nyukayoteiYmd", fields.nyukayoteiYmd);
    meiMethods.setValue(
      "uchiwakeKbn",
      getSCode(HachuUchiwakeKbn, uchiwakeKbn?.value!)
    );
  };

  // 発注金計算
  const handleCalcHachuKin = () => {
    const meiFields = meiMethods.getValues();
    const { uchiwakeKbn } = meiMethods.getValues();

    if (meiFields.hachutanka === "") {
      meiMethods.setValue("hachuKin", "");
      return;
    }

    // 内訳区分が2:値引以外のとき
    if (uchiwakeKbn?.value !== hachuUchiwakeKbn.nebiki) {
      // hachusu と hachutanka が null ではない場合
      if (meiFields.hachusu !== null && meiFields.hachutanka !== null) {
        // hachuKin に設定する。hachusu × hachutanka
        const calcHachuKin: Big = new Big(meiFields.hachusu!)
          .times(new Big(meiFields.hachutanka!))
          .round(0, Big.roundDown);
        meiMethods.setValue("hachuKin", calcHachuKin.toString());
      }
    }
  };

  // 仕入先変更
  const handleChangeShire = () => {
    // フォームのデータを取得する。
    const fields = methods.getValues();
    if (fields.shireCd === "") {
      methods.setValue("shireNm", "");
      methods.setValue("shainCd", "");
      methods.setValue("shainNm", "");
      methods.setValue("bumonCd", "");
      methods.setValue("bumonNm", "");
      methods.setValue("eigyoCd", "");
      methods.setValue("defaultSokoCd", "");
      methods.setValue("defaultSokoNm", "");
      methods.setValue("defaultSokoshubetsuKbn", "");
      methods.setValue("shireShokuchiKbn", undefined);
      return;
    }

    // 変更されていない場合は切替しない。
    if (!methods.getFieldState("shireCd").isDirty) {
      return;
    }
    // 仕入先関連項目設定
    B040010Shire(methods, navigate, setMsg);
    methods.unregister("shireCd", { keepValue: true });
  };

  // お届け先変更
  const handleChangeOtodoke = () => {
    // フォームのデータを取得する。
    const fields = methods.getValues();
    if (fields.otodokeCd === "") {
      methods.setValue("otodokeNm", "");
      methods.setValue("otodokeShokuchiKbn", undefined);
      return;
    }

    // 変更されていない場合は切替しない。
    if (!methods.getFieldState("otodokeCd").isDirty) {
      return;
    }
    // お届け先関連項目設定
    B000170OtodokeNmShokuchi(methods, navigate, setMsg);
    methods.unregister("otodokeCd", { keepValue: true });
  };

  // レイアウト設定
  return (
    <>
      <FormProvider {...methods}>
        <SFrame
          title="発注入力"
          gridTemplateColumns="6rem 10rem 1rem 5rem 10rem 5rem 1rem 6rem 10rem 10rem 10rem"
        >
          <SLabel>発注No.</SLabel>
          <SNumberField name="hachudenpyoNoF" size={10} disabled code />

          <SLabel required gridColumn="1">
            発注日
          </SLabel>
          <SDateField
            label="発注日"
            name="hachuYmd"
            required
            {...register(1)}
            disabled={isReference}
            onBlur={() => handleChangeHachuYmd()}
            onAccept={(date: string) => {
              handleChangeHachuYmd(date);
            }}
          />

          <SLabel gridColumn="4">希望納期</SLabel>
          <SDateField
            name="nyukayoteiYmd"
            {...register(2)}
            disabled={isReference}
          />

          <SLabel required gridColumn="1">
            仕入先
          </SLabel>
          <STextField
            label="仕入先コード"
            name="shireCd"
            required
            size={6}
            {...register(3)}
            onBlur={() => handleChangeShire()}
            dialog={<B000170 />}
            handleSubmit={(data: any) => {
              methods.setValue("shireCd", data.shireCd);
              // 仕入先表示
              B040010Shire(methods, navigate, setMsg);
            }}
            disabled={isReference}
            code
          />
          <STextField
            name="shireNm"
            size={40}
            {...register(4)}
            gridColumn="span 4"
            disabled={!isShireShokuchi || isReference}
          />

          <SLabel gridColumn="1">お届け先</SLabel>
          <STextField
            label="お届け先コード"
            name="otodokeCd"
            size={6}
            {...register(5)}
            onBlur={() => handleChangeOtodoke()}
            dialog={<B000170 />}
            handleSubmit={(data: any) => {
              methods.setValue("otodokeCd", data.shireCd);
              methods.setValue("otodokeNm", data.shireNmr);
              methods.setValue("otodokeShokuchiKbn", data.shokuchiKbn);
            }}
            disabled={isReference}
            code
          />
          <STextField
            name="otodokeNm"
            size={40}
            {...register(6)}
            gridColumn="span 4"
            disabled={!isOtodokeShokuchi || isReference}
          />

          <SLabel gridColumn="1" required>
            発注担当者
          </SLabel>
          <STextField
            label="発注担当者コード"
            name="shainCd"
            required
            size={7}
            {...register(7)}
            onBlur={() => B040010Shain(methods, navigate, setMsg)}
            dialog={<B000140 />}
            handleSubmit={(data: any) => {
              methods.setValue("shainCd", data.shainCd);
              methods.setValue("shainNm", data.shainNm);

              B040010Shain(methods, navigate, setMsg);
            }}
            disabled={isReference}
            code
          />
          <STextField name="shainNm" disabled gridColumn="span 4" size={40} />

          <SLabel gridColumn="8">発注担当部門</SLabel>
          <STextField name="bumonNm" disabled size={20} />

          <SLabel gridColumn="1">発注備考</SLabel>
          <SNumberField
            name="bikoCd"
            onBlur={() => B000111Biko(methods, navigate, setMsg)}
            dialog={<B000111 meishoKbn={meishoKbn.biko} />}
            handleSubmit={(data: any) => {
              methods.setValue("bikoCd", data?.meishoCd);
              methods.setValue("biko", data?.meishoNm1);
              methods.setValue("bikoCdBefore", data?.meishoCd);
            }}
            size={3}
            {...register(8)}
            disabled={isReference}
            code="true"
          />
          <STextField
            name="biko"
            gridColumn="span 4"
            size={40}
            {...register(9)}
            disabled={isReference}
          />
          <SLabel gridColumn="9" gridRow="7">
            発注金額合計
          </SLabel>
          <SLabel gridColumn="10" gridRow="7">
            消費税
          </SLabel>
          <SLabel gridColumn="11" gridRow="7">
            合計
          </SLabel>
          <SNumberField
            name="hachuKinGokei"
            disabled
            align="right"
            gridColumn="9"
            gridRow="8"
            size={11}
          />
          <SNumberField
            name="zeiKinGokei"
            disabled
            align="right"
            gridColumn="10"
            gridRow="8"
            size={10}
          />
          <SNumberField
            name="gokei"
            disabled
            align="right"
            gridColumn="11"
            gridRow="8"
            size={11}
          />
        </SFrame>
      </FormProvider>
      <SFrame>
        <STable
          keyName="hachugyoNo"
          data={rows}
          columns={columns}
          totalCount={rowCount}
          tableRef={tableRef}
          width={1260}
          height={320}
          handleDoubleClick={
            isReference ? undefined : handleDoubleClickTableRow
          }
          rowSelectable={meiMethods.watch("meiTab")}
        />
      </SFrame>

      <FormProvider {...meiMethods}>
        <SFrame>
          <SEntryTab
            name="meiTab"
            handleOk={handleChangeMeiTab}
            disabled={isReference}
          ></SEntryTab>
          <SFrame gridTemplateColumns="7rem 10rem 5rem 9rem 8rem 8rem 8rem 8rem 10.5rem">
            <SLabel required>区分</SLabel>
            <SLabel required>商品コード</SLabel>
            <SLabel required gridColumn="span 2">
              倉庫
            </SLabel>
            <SLabel>発注数</SLabel>
            <SLabel>発注単価</SLabel>
            <SLabel required>発注金額</SLabel>
            <SLabel required>希望納期</SLabel>

            <SCodeSelect
              label="区分"
              name="uchiwakeKbn"
              required
              options={HachuUchiwakeKbn}
              {...register(11)}
              gridColumn="1"
              onChange={() => handleChangeUchiwakeKbn()}
              disabled={isReference}
            />
            <STextField
              label="商品コード"
              name="shohinCd"
              size={10}
              required
              {...register(12)}
              dialog={<B000200 />}
              handleSubmit={(data: any) => {
                meiMethods.setValue("shohinCd", data.shohinCd);
                meiMethods.setValue("shohinNm", data.shohinNm);
                meiMethods.setValue("shohinShokuchiKbn", data.shokuchiKbn);
                // 商品関連項目表示
                B040010Shohin(methods, meiMethods, navigate, setMsg);
              }}
              onBlur={() =>
                // 商品コード変更
                B040010HandleChangeShohinCd(
                  methods,
                  meiMethods,
                  navigate,
                  setMsg
                )
              }
              disabled={isReference}
              code
            />
            <STextField
              label="倉庫コード"
              name="sokoCd"
              {...register(13)}
              size={3}
              required
              onBlur={() =>
                B000190SokoNmrSokoshubetsuKbn(meiMethods, navigate, setMsg)
              }
              dialog={<B000190 />}
              handleSubmit={(data: any) => {
                meiMethods.setValue("sokoCd", data?.sokoCd);
                meiMethods.setValue("sokoNm", data?.sokoNmr);
                meiMethods.setValue("sokoshubetsuKbn", data?.sokoshubetsuKbn);
              }}
              disabled={isReference}
              code
            />
            <STextField name="sokoNm" disabled />

            <SNumberField
              label="発注数"
              name="hachusu"
              size={6}
              align="right"
              {...register(14)}
              disabled={isNebiki || isReference}
              onBlur={() => {
                handleCalcHachuKin();
              }}
              dialog={<B000220 shohinCd={meiMethods.watch("shohinCd")} />}
              handleSubmit={(data: any) => {
                meiMethods.setValue("shohinCd", data.shohinCd);
                meiMethods.setValue("shohinNm", data.shohinNm);
                meiMethods.setValue("sokoCd", data.sokoCd);
                meiMethods.setValue("sokoNm", data.sokoNm);
                meiMethods.setValue("sokoshubetsuKbn", data.sokoshubetsuKbn);

                // 商品関連項目表示
                B040010Shohin(methods, meiMethods, navigate, setMsg);
              }}
            />
            <SNumberField
              label="発注単価"
              name="hachutanka"
              size={6}
              align="right"
              {...register(15)}
              disabled={isNebiki || isReference}
              onBlur={() => {
                handleCalcHachuKin();
              }}
            />
            <SNumberField
              label="発注金額"
              name="hachuKin"
              required
              size={6}
              align="right"
              {...register(16)}
              disabled={!isNebiki || isReference}
            />
            <SDateField
              label="希望納期"
              name="nyukayoteiYmd"
              required
              {...register(17)}
              disabled={isReference}
            />

            <SLabel gridColumn="1/5">商品名</SLabel>
            <SLabel gridColumn="5/8">発注明細備考</SLabel>
            <STextField
              name="shohinNm"
              gridColumn="span 4"
              {...register(18)}
              disabled={!isShohinShokuchiKbn || isReference}
            />
            <SNumberField
              name="meisaibikoCd"
              {...register(19)}
              size={3}
              onBlur={() => B000111Meisaibiko(meiMethods, navigate, setMsg)}
              dialog={<B000111 meishoKbn="7" />}
              handleSubmit={(data: any) => {
                meiMethods.setValue("meisaibikoCd", data?.meishoCd);
                meiMethods.setValue("meisaibiko", data?.meishoNm1);
                meiMethods.setValue("meisaibikoCdBefore", data?.meishoCd);
              }}
              gridColumn="6/5"
              disabled={isReference}
              code="true"
            />
            <STextField
              name="meisaibiko"
              gridColumn="6/8"
              {...register(20)}
              disabled={isReference}
            />

            <SCancelButton
              label={labels.buttons.deleteDetail}
              {...register(22)}
              gridRow="7"
              gridColumn="1"
              onClick={handleClickDeleteMeiButton}
              disabled={disabledDeleteDetail}
            />
            {meiMethods.watch("meiTab") ? (
              <SNormalButton
                label={labels.buttons.createDetail}
                {...register(21)}
                gridRow="7"
                gridColumn="9"
                align="right"
                onClick={handleClickCreateMeiButton}
                disabled={isReference}
              />
            ) : (
              <SNormalButton
                label={labels.buttons.updateDetail}
                {...register(21)}
                gridRow="7"
                gridColumn="9"
                align="right"
                onClick={handleClickUpdateMeiButton}
              />
            )}
          </SFrame>
          <SFrame gridTemplateColumns="7rem 67rem 4rem">
            <SCancelButton
              label={labels.buttons.delete}
              gridColumn={1}
              {...register(24)}
              disabled={!isUpdate}
              dialog={
                <SConfirmDialog
                  title="発注データ削除"
                  msg="発注データを削除します。よろしいですか？"
                />
              }
              dialogAction="confirm"
              handleSubmit={handleClickDeleteButton}
            />
            {!isReference && (
              <>
                {methods.getValues().hachudenpyoNo === "" ? (
                  <SNormalButton
                    label={labels.buttons.create}
                    align="right"
                    {...register(23)}
                    onClick={() => handleOpenCreateConfirm()}
                  />
                ) : (
                  <SNormalButton
                    label={labels.buttons.update}
                    align="right"
                    {...register(23)}
                    onClick={() => handleOpenUpdateConfirm()}
                  />
                )}
              </>
            )}
          </SFrame>
        </SFrame>
        <SConfirmDialogAction
          msg={labels.dialog.createCheck}
          onOk={handleClickCreateButton}
          onCancel={handleCancelCreateConfirm}
          isOpen={openCreateConfirm}
        />
        <SConfirmDialogAction
          msg={labels.dialog.updateCheck}
          onOk={handleClickUpdateButton}
          onCancel={handleCancelUpdateConfirm}
          isOpen={openUpdateConfirm}
        />
      </FormProvider>
    </>
  );
};
